/**
 * CompanyScreen - Dedicated page showing company-exclusive content.
 *
 * Renders the company's explore area structurally the same way the native app
 * (kinasticCoach) does: the company-private root category (identifier === gymId)
 * holds no entries directly — its content is nested under sub-categories. So we
 * resolve the company root, then render its DIRECT sub-categories through the
 * shared <ExploreCategorySwitch>, exactly like AcademyScreen. Each sub-category
 * renderer (big_preview / preview / card_link / category_group) fetches its own
 * entries one level deeper (parentId = sub-category id), surfacing e.g. the
 * "Healthy Menu Plan" months. Previously this screen fetched entries at the
 * company-root parentId, which always returned [] (no entries sit directly under
 * the root) and rendered "No content available yet."
 *
 * Created by claude on 09.03.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Container } from 'reactstrap';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useRootStore } from '../../Store/useRootStore';
import { ExploreCategory, ExploreCategoryApi } from '../../Model/Explore/ExploreCategory';
import { ExploreCategorySwitch } from '../../Components/ExploreCards/ExploreCategorySwitch';
import { useCompanySubCategoriesSuspense } from '../../Hooks/useCompanySubCategoriesSuspense';
import { getMediaSmallest } from '../../Model/Media/Media';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Theme } from '../../Theme/Theme';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageWrapper = styled.div`
  padding-bottom: 64px;
  animation: ${fadeInUp} 0.5s ease-out;
`;

const HeaderSection = styled(Container)`
  max-width: 1200px;
  padding: 48px 24px 8px;

  @media (max-width: 768px) {
    padding: 32px 16px 8px;
  }
`;

const CompanyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const LogoCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 32px;
  background: ${Theme.Colors.lightestGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LogoFallback = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${Theme.Colors.primary};
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  line-height: 1.6;
  margin: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${Theme.Colors.darkGrey};
  font-size: 1rem;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${Theme.Colors.darkGrey};
  font-size: 1rem;
`;

/**
 * Renders the company root's direct sub-categories. Each <ExploreCategorySwitch>
 * dispatches by category.type and fetches its own entries/children one level
 * deeper, so the nested company content surfaces without any manual entry fetch
 * here. Empty entry-bearing sections collapse themselves (return null).
 */
const CompanySubCategories: React.FC<{ rootId: string }> = observer(({ rootId }) => {
  const { t } = useTrans();
  const { categories } = useCompanySubCategoriesSuspense(rootId);

  if (categories.length === 0) {
    return <EmptyState>{t('company.noContent', { defaultValue: 'No content available yet.' })}</EmptyState>;
  }

  return (
    <>
      {categories.map((category, index) => (
        <ExploreCategorySwitch
          key={category.id}
          category={category}
          categoryIndex={index}
          isLast={index === categories.length - 1}
          linkPrefix="/company/"
        />
      ))}
    </>
  );
});

export const CompanyScreen: React.FC = observer(() => {
  const { gymCustomer } = useRootStore();
  const { t } = useTrans();

  const [categories, setCategories] = useState<ExploreCategory[]>([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  const customer = gymCustomer.customer;
  const gym = gymCustomer.currentGym;

  // Fetch the root explore categories to locate this gym's company-private root.
  useEffect(() => {
    if (customer?.gymId) {
      ExploreCategoryApi.find({ root: true })
        .then(setCategories)
        .catch(() => setCategories([]))
        .finally(() => setCategoriesLoaded(true));
    }
  }, [customer?.gymId]);

  // Find the company category (company root: identifier === gymId — see companyCategory.ts).
  const companyCategory = useMemo(() => {
    if (!customer?.gymId) return undefined;
    return (
      categories.find((c) => c.identifier === customer.gymId) ??
      categories.find((c) => c.gymId === customer.gymId)
    );
  }, [categories, customer?.gymId]);

  // Get the gym logo
  const gymLogoUrl = useMemo(() => {
    if (gym?.logo) {
      return getMediaSmallest(gym.logo);
    }
    return undefined;
  }, [gym?.logo]);

  const companyName =
    companyCategory?.translatedName || gym?.name || t('home.companyArea.title', { defaultValue: 'Company' });

  if (!customer?.gymId) {
    return (
      <PageWrapper>
        <EmptyState>{t('company.noCompany', { defaultValue: 'No company content available.' })}</EmptyState>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HeaderSection>
        <CompanyHeader>
          <LogoCircle>
            {gymLogoUrl ? (
              <LogoImage src={gymLogoUrl} alt={companyName} />
            ) : (
              <LogoFallback>{companyName?.charAt(0)?.toUpperCase() ?? 'C'}</LogoFallback>
            )}
          </LogoCircle>
          <HeaderContent>
            <Title>{companyName}</Title>
            <Subtitle>
              {t('home.companyArea.subtitle', { defaultValue: 'Exclusive content for your company' })}
            </Subtitle>
          </HeaderContent>
        </CompanyHeader>
      </HeaderSection>

      {companyCategory ? (
        <Suspense fallback={<LoadingState>{t('common.loading', { defaultValue: 'Loading...' })}</LoadingState>}>
          <CompanySubCategories rootId={companyCategory.id} />
        </Suspense>
      ) : categoriesLoaded ? (
        <EmptyState>{t('company.noContent', { defaultValue: 'No content available yet.' })}</EmptyState>
      ) : (
        <LoadingState>{t('common.loading', { defaultValue: 'Loading...' })}</LoadingState>
      )}
    </PageWrapper>
  );
});
